import { Alert, Container, Stack, Typography } from "@mui/material";
import { FaqElement, NextPageWithLayout } from "@cm-websites/common";
import { NavigationAndFooterLayout } from "../layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../next-i18next.config";
import NextLink from "next/link";
import React from "react";
import type { GetStaticProps } from "next";

export const FaqPage: NextPageWithLayout = () => (
  <React.Fragment>
    <Container
      maxWidth="md"
      sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}
    >
      <Typography variant="h1" sx={{ pb: 6 }}>
        Frequent Questions
      </Typography>

      <Typography variant="body1" paragraph>
        We’ve collected together some of the questions people frequently ask about our murder mystery
        dinner games. If you have a question which is not listed here, please do contact us.
      </Typography>

      <Alert severity="warning">
        If one of your game booklets has gone <NextLink href="/missing-materials">missing</NextLink>, or
        you would like to download the <NextLink href="/preparation">role descriptions</NextLink> so you
        can send them to your guests, you can do this in our download area.
      </Alert>

      <Stack direction="column" spacing={1} sx={{ pt: 6 }}>
        <FaqElement question="Your games specify a different number of players, for example 5 – 7, how does this work?">
          Our murder mystery games were developed for a variable number of players. The game manual
          contains sealed sections depending on the number of players. You simply open the section which
          corresponds to the number of players you have (e.g. 6 players). This will tell you which roles
          you need to assign, and which you can leave out. Please note that our games are not all
          intended for 5 – 7 players. We also have games for 5 – 8 and 6 – 8 players. The exact number
          can be found on the product page of the game in question.
        </FaqElement>
        <FaqElement question="I have no experience with role-playing games. How hard is it?">
          You do not need any experience with role-playing games. The important thing for a murder
          mystery game is that you enjoy discussing and investigating. That’s just what Culinario Mortale
          is about.
        </FaqElement>
        <FaqElement question="One of my game booklets has gone missing. What should I do?">
          Don’t panic! We have already planned for eventualities such as this. Every game includes a
          unique support code. This will allow you to download lost game materials and print them out
          yourself at home.
        </FaqElement>
        <FaqElement question="What does ‘male characters can be played by women and vice versa’ mean?">
          In our experience, any role can be played just as well by anyone regardless of gender. This
          doesn’t mean the gender of the character changes – You shouldn’t turn Patrick into Patricia!
          This could cause problems in the story and cause players to get confused. Instead, we can
          simply reassure you that any character can be played by anyone. In our experience, this just
          makes playing the game all the more fun.
        </FaqElement>
        <FaqElement question="Do I need to be able to act?">
          No, you don’t need a particular talent for acting! Carefully reading your character’s
          description before the game begins is more than enough. Murder mystery games place the focus on
          discussion and having fun with the other players. If you wish, you may like to dress up to
          match your role. Have a look at your character’s illustration and description for inspiration.
          In our experience, costumes make the game a lot more fun and make it much easier to really get
          into the roles.
        </FaqElement>
        <FaqElement question="Does the murderer know right from the start that they are the murderer?">
          Yes, every player knows right from the start whether they are guilty or innocent. Of course,
          all of the characters have their own dark secrets which they want to keep to themselves.
        </FaqElement>
        <FaqElement question="Do I have to dress up?">
          No, it is not necessary to dress up. But perhaps you have an idea for a cool outfit that
          matches your character. It doesn’t just help you get into your role – it’s also a lot of fun.
        </FaqElement>
        <FaqElement question="How long do your murder mystery parties take?">
          This depends entirely on how talkative the players are. But in our experience, a game takes
          around 2 – 4 hours including breaks and allowing enough time to eat.
        </FaqElement>
        <FaqElement question="What age are the games appropriate for?">
          There is of course no upper limit in terms of the age of the players. But in order to cast
          characters in a realistic manner, players should be at least 16 years of age. Please also note
          that the content is not always appropriate for children, given the amorous encounters between
          some of the characters.
        </FaqElement>
        <FaqElement question="Übernehme ich als Gastgeber auch eine Rolle? Kann ich also mitspielen?">
          Ja. Als Gastgeber kannst du dir eine der zu verteilenden Rollen aussuchen. Für dich als
          Gastgeber gelten die gleichen Spielregeln wie für alle Mitspieler.
        </FaqElement>
        <FaqElement question="As a host, how can I prepare for the evening?">
          Please take your time to read the game manual. It’s concise and clearly written, and contains
          everything you need to know. If you don’t have your murder mystery game yet, you can read the
          information on our website. The game manual will help you distribute the roles in advance –
          either personally or conveniently via email. Apart from that, the only thing left to prepare
          are the finishing touches. The decorations and something tasty to eat.
        </FaqElement>
        <FaqElement question="I can’t cook! Do I really need to prepare food?">
          You don’t have to be a five-star chef. But since a game can last up to four hours, your guests
          will definitely be grateful if you can offer them something to eat. We have created a number of
          menu suggestions for you. And if you really can’t stand cooking… Ok, we admit it… We may have
          ordered pizza when we tested the games – guilty as charged!
        </FaqElement>
        <FaqElement question="Are the cases available in other languages?">
          Yes, you can also get our crime games in German and French. And, if you live in Japan, there is
          also a Japanese language version.
        </FaqElement>
        <FaqElement question="As the host, can I organise the game without knowing who the perpetrator is?">
          Yes, you will not learn any secret information when organising the mystery dinner and you will
          not know who the perpetrator is. The same rules apply to you as to your fellow players. Since
          you choose one of the roles to be distributed for yourself, it can even happen that you take on
          the role of the perpetrator.
        </FaqElement>
      </Stack>
    </Container>
  </React.Fragment>
);

FaqPage.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default FaqPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale
        ? await serverSideTranslations(locale, ["c-common", "c-user-consent-management"], i18nConfig)
        : {}),
    },
  };
};
